/**
 * Payload returned by the `getDocumentDownloadUrl` callable.
 *
 * File-backed documents (and large derived documents with a Storage sidecar) return a signed
 * `downloadUrl`. Documents with no file return their text as `inlineText` with a null URL: small
 * derived write-backs (Deal Wiki, dossier, IC Committee/memo; markdown, with confidence displays
 * removed), and verbatim text that is never rewritten: forwarded email bodies and portfolio report
 * narratives (derived, markdown, their author's words) and source-written meeting transcripts
 * (Fireflies/Granola/Otter, plain text under their own name).
 */
export interface DocumentDownloadResponse {
  downloadUrl?: string | null;
  inlineText?: string | null;
  fileName?: string;
}

/**
 * Inline documents are AI-generated from untrusted source files, and a blob: URL shares the app's
 * origin. They are therefore only ever created as text/plain — never HTML/markdown — so a browser
 * tab renders them as inert text and a `download` anchor just saves the bytes.
 */
const INLINE_CONTENT_TYPE = 'text/plain; charset=utf-8';

export function isBlobUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.startsWith('blob:');
}

/**
 * Resolve a callable response to a URL the browser can fetch, open or download.
 *
 * Inline text becomes a `blob:` URL. Pass `revokeAfterMs` for fire-and-forget uses (new tab,
 * download); omit it when the caller owns the lifetime and revokes it itself (the source drawer).
 * Returns null when the response carries neither a URL nor inline text.
 */
export function resolveDocumentUrl(
  response: DocumentDownloadResponse | null | undefined,
  options: { revokeAfterMs?: number } = {},
): string | null {
  if (response?.downloadUrl) return response.downloadUrl;
  if (typeof response?.inlineText !== 'string' || !response.inlineText) return null;

  const url = URL.createObjectURL(new Blob([response.inlineText], { type: INLINE_CONTENT_TYPE }));
  if (options.revokeAfterMs !== undefined) {
    setTimeout(() => URL.revokeObjectURL(url), options.revokeAfterMs);
  }
  return url;
}

export function triggerBrowserDownload(url: string, fileName?: string): void {
  if (!url) {
    throw new Error('Download URL is required');
  }

  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';

  if (fileName) {
    link.download = fileName;
  }

  const parent = document.body || document.documentElement;
  parent.appendChild(link);

  try {
    link.click();
  } finally {
    link.remove();
  }
}
